import httpClient from '@/services/httpClient'
import type { ConexaResponse } from '@/services/httpClient'
import type {
  ClinicOfficeHoursResponse,
  ClinicOfficeHoursResponsePt,
  AttendanceLimitResponse,
  IScheduledPVAttendance,
  IScheduledPVAttendanceResponse,
  GetQueueSizeResponsePT,
  GetQueueSizeResponse,
  IScheduledGroupAttendanceResponse,
  IScheduledGroupAttendance,
  AttendanceChange,
  GetAttendanceChangesResponse,
  RecentProfessionalsData,
  RecentProfessionalsResponse,
  ConsultationWithPendingConfirmation,
  GetConsultationWithPendingConfirmationResponse,
  ExamRequestResponse,
} from './types'

export default {
  getPvUrl(): Promise<string> {
    return httpClient
      .post('/integration/enterprise/patients/register-to-partner')
      .then(res => res.data.object)
  },
  async getPvAttendances(): Promise<IScheduledPVAttendance[]> {
    const fetchedPvAttendances = await httpClient
      .get<ConexaResponse<IScheduledPVAttendanceResponse[]>>(
        '/integration/psicologia-viva/atendimento/atendimentos-do-dia',
      )
      .then(d => d.data.object)

    const translatedPvAttendances = fetchedPvAttendances?.map(attendance => ({
      date: attendance?.dataConsulta,
      id: attendance?.id,
      hasSelfie: attendance?.possuiFotoSelfie,
      attendanceRoomUrl: attendance?.salaAtendimentoUrl,
      status: attendance?.status,
      consultationLength: attendance?.tempoConsulta,
      patient: {
        id: attendance?.paciente.id,
      },
      professional: {
        celular: attendance?.profissional?.celular,
        email: attendance?.profissional?.email,
        id: attendance?.profissional?.id,
        name: attendance?.profissional?.nome,
        profile: attendance?.profissional?.perfil,
        phone: attendance?.profissional?.telefone,
        profileUrl: attendance?.profissional?.urlPerfil,
      },
    }))

    return translatedPvAttendances
  },
  async getTodayAttendancesGroup(): Promise<IScheduledGroupAttendance[]> {
    const fetchedAttendancesGroup = await httpClient
      .get<ConexaResponse<IScheduledGroupAttendanceResponse[]>>(
        '/appointment-group/list-by-patient?atDay=1&patientConfirmed=1',
      )
      .then(d => d.data.object)
    return fetchedAttendancesGroup
  },
  async getNextAttendanceGroup(): Promise<IScheduledGroupAttendance> {
    const fetchedNextAttendanceGroup = await httpClient
      .get('/appointment-group/next-pending')
      .then(response => response.data.object)
    return fetchedNextAttendanceGroup
  },
  async getClinicOfficeHours(
    clinicId: number,
  ): Promise<ClinicOfficeHoursResponse[]> {
    const { data } = await httpClient.get<
      ConexaResponse<ClinicOfficeHoursResponsePt[]>
    >(`/integration/app-paciente/clinica/${clinicId}/expediente`)

    const clinicOfficeHours: ClinicOfficeHoursResponse[] = data.object?.map(
      item => ({
        beginDate: item?.horaInicio,
        endDate: item?.horaFim,
      }),
    )

    return clinicOfficeHours
  },
  async getAttendanceLimit(props: {
    date?: string
    council?: string
    attendanceType?: string
    professionalType?: string
    specialityId?: string | number
    rescheduleToken?: string
  }): Promise<AttendanceLimitResponse> {
    const PARAM_MAP_ENG_TO_PT = {
      date: 'dataAgendamento',
      council: 'tipoConselho',
      professionalType: 'tipoProfissional',
      attendanceType: 'modoAtendimento',
      specialityId: 'idEspecialidade',
      rescheduleToken: 'tokenAtendimentoReagendamento',
    }
    const params = Object.entries(props)
      ?.map(item => {
        if (!item[1]) {
          return null
        }
        return `${PARAM_MAP_ENG_TO_PT[item[0]]}=${item[1]}`
      })
      ?.filter(item => !!item)
      ?.join('&')

    return httpClient
      .get(`/integration/app-paciente/atendimento/possui-limite?${params}`)
      .then(res => {
        if (res.status === 204) return 'hasNoFreeAttendanceAllowed'
        if (res.data.object) return 'limitNotReached'
        if (res.data.object === false) return 'limitReached'
      })
  },

  getQueueSize: async (speciality: string): Promise<GetQueueSizeResponse> => {
    const { data } = await httpClient.get<
      ConexaResponse<GetQueueSizeResponsePT>
    >(`/fila/atendimento/tamanho-fila/${speciality}`)

    const parsedResponse: GetQueueSizeResponse = {
      queueSize: data.object.tamanhoFila,
      rawWaitTime: data.object.tempoEsperaNaoArredondado,
      waitTime: data.object.tempoEspera,
      maxWaitTime: data.object.tempoMaximoEspera,
      minWaitTime: data.object.tempoMinimoEspera,
    }

    return parsedResponse
  },
  getAttendanceRealized: async (): Promise<boolean> => {
    const { data } = await httpClient.get<ConexaResponse<boolean>>(
      '/integration/app-paciente/possui-atendimento-realizado',
    )
    return data.object
  },
}

export const getAttendanceChange = async (): Promise<AttendanceChange> => {
  const { data } = await httpClient.get<GetAttendanceChangesResponse>(
    '/integration/app-paciente/notificacao-consulta',
  )

  return {
    id: data?.object?.id,
    clinicId: data?.object?.idClinica,
    patientId: data?.object?.idPaciente,
    hasCancelled: data?.object?.consultaCancelada,
    hasRescheduled: data?.object?.consultaReagendada,
    createdAt: data?.object?.momentoCriacao,
    lastNotificationAt: data?.object?.momentoUltimaNotificacao,
  }
}

export const cleanAttendanceChange = async (): Promise<void> => {
  await httpClient.delete('/integration/app-paciente/notificacao-consulta')
}

export const checkProfessionalAvailability = async (
  professionalId: number,
): Promise<boolean> => {
  const response = await httpClient.get<ConexaResponse<boolean>>(
    `/integration/app-paciente/profissional/agenda/disponivel?idProfissional=${professionalId}`,
  )

  return response.data.object
}

export const getRecentProfessionals = async (): Promise<
  RecentProfessionalsData[]
> => {
  const { data } = await httpClient.get<
    ConexaResponse<RecentProfessionalsResponse[]>
  >('/integration/app-paciente/profissional/listar/ultimos/profissionais')
  return data.object.map(item => ({
    professionalId: item?.idProfissional,
    professionalName: item?.nomeProfissional,
    speciality: item?.especialidade,
    council: item?.tipoConselho,
    professionalType: item?.tipoProfissional,
    hasSchedule: item?.temAgenda,
    photoUrl: item?.urlFoto,
    performedLastService: item?.realizouUltimoAtendimento,
  }))
}

export const getConsultationsWithPendingConfirmation = async (): Promise<
  ConsultationWithPendingConfirmation[]
> => {
  const { data } =
    await httpClient.get<GetConsultationWithPendingConfirmationResponse>(
      '/integration/app-paciente/atendimento/listar/alerta/consultas-pendente-confirmacao',
    )

  return data?.object?.map(consulta => {
    return {
      id: consulta?.idAtendimento,
      dateTime: consulta?.dataHoraAtendimento,
      professionalName: consulta?.nomeProfissional,
      speciality: consulta?.especialidade,
    }
  })
}

export const getExamsRequests = async (): Promise<ExamRequestResponse> => {
  const { data } = await httpClient.get<ConexaResponse<ExamRequestResponse>>(
    '/patient-exams/schedule/exams-requests',
  )

  return data?.object
}

export const checkPatientTeamProfessional = async (
  token: string,
): Promise<boolean> => {
  const { data } = await httpClient.get<ConexaResponse<boolean>>(
    '/primary-health-care/patient-team-has-professional',
    {
      headers: {
        token: token,
      },
    },
  )

  return data?.object
}
